import type { PimWorkflowDetail } from "@nova-ai/shared"
import { formatDateTime } from "../../lib/format-date"
import type { WorkflowHistorySearch } from "../../services/use-pim-workflow-history"
import { Button } from "../ui/button"
import { WorkflowReceiptSummary } from "./workflow-history-evidence"
import { WorkflowHistoryProgress } from "./workflow-history-progress"
import {
  groupWorkflowScopes,
  scopeFailures,
  workflowKindLabel,
} from "./workflow-history-presentation"

type DetailProps = {
  data: PimWorkflowDetail
  search: WorkflowHistorySearch
  onChange: (search: WorkflowHistorySearch) => void
}

export function WorkflowHistoryDetail({ data, search, onChange }: DetailProps) {
  const groups = groupWorkflowScopes(data.targets)
  const displayedStores = new Set(data.targets.map((target) => target.store)).size
  const hiddenFailure =
    data.receipt.summary.failed &&
    data.receipt.validation !== "rejected" &&
    !data.targets.some((target) => scopeFailures(target).length > 0)
  return (
    <section aria-label={`Receipt ${data.receipt.id}`} className="space-y-4">
      <header className="space-y-3">
        <Button
          onClick={() =>
            onChange({
              ...search,
              receiptId: undefined,
              targetAfter: undefined,
              attemptAfter: undefined,
            })
          }
        >
          All receipts
        </Button>

        <div>
          <h2 className="text-[15px] font-semibold">
            {`Receipt #${data.receipt.id} · Product ${data.receipt.productId ?? "unresolved"}`}
          </h2>

          <p className="mt-1 break-words text-xs text-muted-foreground">
            {`${data.receipt.sku ?? "SKU unresolved"} · ${workflowKindLabel(data.receipt.kind)} · Received ${formatDateTime(data.receipt.receivedAt)}`}
          </p>
        </div>
      </header>

      <WorkflowReceiptSummary receipt={data.receipt} />

      {hiddenFailure && (
        <div className="space-y-2">
          <p className="max-w-prose text-sm text-destructive">
            Failure details may be on another page or outside your filters. A receipt-level failure
            may have no store evidence.
          </p>

          <Button onClick={() => onChange({ limit: search.limit, receiptId: data.receipt.id })}>
            Clear receipt filters
          </Button>
        </div>
      )}

      <section aria-label="Store progress" className="space-y-3">
        <h3 className="text-sm font-semibold">Store progress</h3>

        <p className="text-xs text-muted-foreground">{`${displayedStores} displayed ${displayedStores === 1 ? "store" : "stores"} · ${data.targets.length} displayed ${data.targets.length === 1 ? "scope" : "scopes"} · ${data.receipt.summary.scopes} total scopes`}</p>

        {groups.map(({ key, members }) => (
          <WorkflowHistoryProgress key={key} members={members} receipt={data.receipt} />
        ))}

        {groups.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No store evidence on this page. Clear filters or return to the first target page.
          </p>
        )}

        <TargetNavigation data={data} search={search} onChange={onChange} />
      </section>

      <p className="text-xs text-muted-foreground">
        Made current means Nova selected that image as the current image. Published means accepted
        by Pub/Sub; delivery is unconfirmed.
      </p>

      {data.receipt.coverage !== "instrumented" && (
        <p className="text-xs text-muted-foreground">
          {data.receipt.coverage === "expired"
            ? "Some retained evidence has expired."
            : "Receipt history may be incomplete. Known steps remain visible."}
        </p>
      )}
    </section>
  )
}

function TargetNavigation({ data, search, onChange }: DetailProps) {
  return (
    <nav aria-label="Store progress pages" className="flex flex-wrap gap-2">
      {search.targetAfter && (
        <Button onClick={() => onChange({ ...search, targetAfter: undefined })}>
          First target page
        </Button>
      )}

      {data.nextTargetCursor && (
        <Button
          onClick={() => onChange({ ...search, targetAfter: data.nextTargetCursor ?? undefined })}
        >
          Next targets
        </Button>
      )}

      {data.targets.length === 0 && (
        <Button onClick={() => onChange({ limit: search.limit, receiptId: data.receipt.id })}>
          Show all receipt stores
        </Button>
      )}
    </nav>
  )
}
